import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["Philosophy", "Spirituality", "Science", "Personal Reflection"];

export default function AdminEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    body: "",
    category: "Personal Reflection",
    cover_image: "",
    published: true,
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data: list } = await api.get("/admin/posts");
        const p = list.find((x) => x.id === id);
        if (!p) {
          toast.error("Entry not found");
          navigate("/admin");
          return;
        }
        setForm({
          title: p.title,
          excerpt: p.excerpt || "",
          body: p.body || "",
          category: p.category,
          cover_image: p.cover_image || "",
          published: p.published,
        });
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/posts/${id}`, form);
        toast.success("Saved.");
      } else {
        await api.post("/posts", form);
        toast.success("Published.");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-32 text-center text-sm text-black/40 tracking-widest uppercase">
        Loading…
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 lg:px-24 pt-16" data-testid="admin-editor">
      <Link
        to="/admin"
        className="text-[10px] uppercase tracking-[0.28em] text-black/45 link-underline"
      >
        ← Back to dashboard
      </Link>

      <h1 className="mt-6 font-serif text-4xl md:text-5xl font-light tracking-tighter">
        {isEdit ? "Edit entry" : "New entry"}
      </h1>

      <form onSubmit={onSubmit} className="mt-12 max-w-3xl space-y-10" data-testid="post-form">
        <div>
          <label className="text-[10px] uppercase tracking-[0.28em] text-black/55">
            Title
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            className="mt-2 w-full bg-transparent border-b border-black/20 py-3 font-serif text-3xl focus:outline-none focus:border-black"
            data-testid="post-title-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-black/55">
              Category
            </label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="mt-2 w-full bg-transparent border-b border-black/20 py-3 focus:outline-none focus:border-black"
              data-testid="post-category-select"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-black/55">
              Cover image URL (optional)
            </label>
            <input
              type="url"
              value={form.cover_image}
              onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
              placeholder="https://…"
              className="mt-2 w-full bg-transparent border-b border-black/20 py-3 focus:outline-none focus:border-black"
              data-testid="post-cover-input"
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.28em] text-black/55">
            Excerpt
          </label>
          <textarea
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            rows={2}
            className="mt-2 w-full bg-transparent border-b border-black/20 py-3 resize-none focus:outline-none focus:border-black"
            data-testid="post-excerpt-input"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.28em] text-black/55">
            Body
          </label>
          <p className="mt-1 text-xs text-black/40">
            Press Enter once for a line break, twice (blank line) for a new paragraph. Leading spaces are preserved (use them to indent). *italic* and **bold** are supported.
          </p>
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            rows={18}
            required
            className="mt-2 w-full bg-transparent border border-black/15 p-4 font-serif text-lg leading-relaxed resize-y focus:outline-none focus:border-black"
            data-testid="post-body-input"
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            id="published"
            type="checkbox"
            checked={form.published}
            onChange={(e) => setForm({ ...form, published: e.target.checked })}
            className="w-4 h-4 accent-black"
            data-testid="post-published-toggle"
          />
          <label
            htmlFor="published"
            className="text-[11px] uppercase tracking-[0.22em] text-black/65"
          >
            Publish publicly
          </label>
        </div>

        <div className="flex items-center gap-4 pt-4 border-t border-black/10">
          <button
            type="submit"
            disabled={saving}
            className="bg-black text-white px-8 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-black/85 transition-colors disabled:opacity-50"
            data-testid="post-save-btn"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Publish entry"}
          </button>
          <Link
            to="/admin"
            className="px-6 py-4 text-[11px] uppercase tracking-[0.28em] text-black/55 hover:text-black"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
